import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { AccessTime as AccessTimeIcon, VolumeUp as VolumeUpIcon } from '@mui/icons-material';
import speechService from '../../services/speechService';

// Componente para a senha atual
const SenhaChamada = ({ senha, guiche, preferencial, horarioChamada, vocalizacaoAutorizada = false }) => {
  const [carregandoAudio, setCarregandoAudio] = useState(false);
  // Usar uma ref para controlar se já existe uma síntese em andamento
  const synthesisActiveRef = useRef(false);
  // Ref para armazenar a última senha anunciada
  const lastAnnouncedRef = useRef({ senha: null, guiche: null });
  // Ref para armazenar o controlador de áudio atual
  const audioControllerRef = useRef(null);

  const anunciarSenha = async () => {
    // Não prosseguir se não tiver senha/guichê ou se já estiver carregando
    if (!senha || !guiche || carregandoAudio || synthesisActiveRef.current) return;
    
    // Verificar se a vocalização está autorizada
    if (!vocalizacaoAutorizada) {
      console.log('Vocalização não autorizada pelo usuário');
      return;
    }
    
    // Verificar se esta senha já está sendo vocalizada pelo PainelChamada
    // Isto ajuda a evitar duplicação de vocalização quando o evento websocket é processado
    const ultimaSenhaVocalizada = localStorage.getItem('ultima_senha_vocalizada');
    if (ultimaSenhaVocalizada) {
      try {
        const senhaData = JSON.parse(ultimaSenhaVocalizada);
        // Se a mesma senha foi vocalizada nos últimos 3 segundos, pular
        if (senhaData.senha === senha && senhaData.guiche === guiche && 
            (Date.now() - senhaData.timestamp) < 3000) {
          console.log('Senha já vocalizada recentemente pelo painel, ignorando duplicação');
          return;
        }
      } catch (e) {
        // Erro ao processar o JSON, continuar com a vocalização
        console.warn('Erro ao processar última senha vocalizada:', e);
      }
    }
    
    try {
      console.log('Iniciando anúncio da senha:', senha, 'guichê:', guiche);
      // Registrar que estamos carregando o áudio
      setCarregandoAudio(true);
      synthesisActiveRef.current = true;
      
      // Registrar esta senha como a última vocalizada
      localStorage.setItem('ultima_senha_vocalizada', JSON.stringify({
        senha: senha,
        guiche: guiche,
        timestamp: Date.now()
      }));
      
      // Limpar qualquer áudio em reprodução antes de iniciar um novo
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      
      // Limpar controlador anterior se existir
      if (audioControllerRef.current) {
        try {
          audioControllerRef.current.dispose();
        } catch (err) {
          console.warn('Erro ao limpar áudio anterior:', err);
        }
        audioControllerRef.current = null;
      }

      // Usar o serviço de síntese de voz do gTTS, passando os parâmetros especiais
      try {
        // Chamar o serviço de voz com as opções especiais para gTTS
        const controller = await speechService.speak('', {
          numero: senha,
          guiche: guiche,
          // Opções para fallback Web Speech API
          rate: 0.9,
          volume: 1.0,
          pitch: 1.0,
          lang: 'pt-BR'
        });
        
        // Armazenar o controlador para uso posterior
        audioControllerRef.current = controller;
        
        // Armazenar esta senha como a última anunciada com sucesso
        lastAnnouncedRef.current = { senha, guiche };
        
      } catch (error) {
        console.error('Erro na síntese de voz:', error);
        
        // Se falhar, tenta uma abordagem de fallback formatando manualmente o texto
        try {
          // Converter a senha para um formato mais claro de ser vocalizado
          const converterParaVoz = (caractere) => {
            // Converter números para texto por extenso
            switch (caractere) {
              case '0': return 'ZERO';
              case '1': return 'UM';
              case '2': return 'DOIS';
              case '3': return 'TRÊS';
              case '4': return 'QUATRO';
              case '5': return 'CINCO';
              case '6': return 'SEIS';
              case '7': return 'SETE';
              case '8': return 'OITO';
              case '9': return 'NOVE';
              default: return caractere; // Letras permanecem iguais
            }
          };
          
          // Converter cada caractere da senha e juntar com espaços
          const senhaSoletrada = senha.split('').map(converterParaVoz).join('-');
          
          // Formatação da mensagem manualmente
          const mensagem = `Atenção. senha número ${senhaSoletrada}. dirija-se ao guichê de número ${guiche}`;
          
          // Tentar usar Web Speech API diretamente
          if (window.speechSynthesis) {
            const utterance = new SpeechSynthesisUtterance(mensagem);
            utterance.lang = 'pt-BR';
            utterance.rate = 0.9;
            utterance.volume = 1.0;
            utterance.pitch = 1.0;
            
            // Disparar síntese
            window.speechSynthesis.speak(utterance);
            
            // Registrar como última senha anunciada mesmo usando abordagem alternativa
            lastAnnouncedRef.current = { senha, guiche };
          }
        } catch (fallbackError) {
          console.error('Erro na abordagem de fallback:', fallbackError);
        }
      }
      
      // Configurar um timeout de segurança para liberar o botão após 10 segundos
      setTimeout(() => {
        if (synthesisActiveRef.current) {
          console.log('Timeout de segurança ativado para liberar o botão');
          synthesisActiveRef.current = false;
          setCarregandoAudio(false);
        }
      }, 10000);
      
    } catch (error) {
      console.error('Erro geral ao anunciar senha:', error);
    } finally {
      // Sempre limpar o estado de carregamento após um tempo
      setTimeout(() => {
        setCarregandoAudio(false);
        synthesisActiveRef.current = false;
      }, 2000);
    }
  };
  
  // Limpar recursos quando o componente for desmontado
  useEffect(() => {
    return () => {
      // Limpar qualquer áudio em reprodução ao desmontar o componente
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      
      if (audioControllerRef.current) {
        try {
          audioControllerRef.current.dispose();
        } catch (err) {
          console.warn('Erro ao limpar áudio ao desmontar:', err);
        }
      }
      

    };
  }, []);
  
  return (
    <Box sx={{ textAlign: 'center', p: 2, position: 'relative' }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', fontSize: '2rem', color: 'text.primary' }}>
        Senha Atual
      </Typography>
      <Typography variant="h1" sx={{ fontSize: '8rem', fontWeight: 'bold', color: 'text.primary', lineHeight: 1.1 }}>
        {senha || '---'}
      </Typography>
      
      {/* Tag Preferencial/Comum */}
      <Box sx={{ 
        display: 'inline-block', 
        px: 4, 
        py: 1.5, 
        borderRadius: 3, 
        bgcolor: preferencial ? '#f44336' : '#2196f3',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1.8rem',
        mb: 2,
        mt: 2,
        boxShadow: preferencial ? '0 4px 20px rgba(244, 67, 54, 0.4)' : '0 4px 20px rgba(33, 150, 243, 0.4)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        minWidth: '200px'
      }}>
        {preferencial ? 'PREFERENCIAL' : 'COMUM'}
      </Box>
      
      <Typography variant="h3" sx={{ mt: 2, color: 'text.primary', fontWeight: 'bold', fontSize: '2.5rem' }}>
        Guichê {guiche || '--'}
      </Typography>
      
      {/* Hora da chamada */}
      {horarioChamada && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mt: 1,
          color: 'text.secondary'
        }}>
          <AccessTimeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            Chamada às {horarioChamada}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SenhaChamada;